import {
    validateRequiredField,
    validateDateField,
    validateTimeFieldFromString,
    validatePhoneNumber,
    validateZipCode,
    requiredIfCondition,
} from '../../../../common/validationHelpers';
import { ChangeableModel } from '../../../../model/model';
import { TResourceValue } from '../../../../services/valuesManager/valuesManager';
import {
    ILanguageState,
    orderItemValidationErrorFieldLabel,
    orderItemValidationErrorFieldName,
} from './LanguageState';
import { FieldValidator, SectionSystemCode, TRange } from '../../../../common/types';
import dayjs from 'dayjs';
import { referralModel } from '../../../ReferralModel';
import { IME_DEFAULT_VENDOR_NOTES } from '../transportation/constants';
import { SuggestedText } from '../../../../components/misc/MultilineTextSuggestion';

export const sectionSystemCode: SectionSystemCode = 'LanguageTranslation';

export const appointmentDateRange = (): TRange<Date> => {
    return {
        from: dayjs(new Date()).add(1, 'day').startOf('day').toDate(),
        to: dayjs(new Date()).add(1, 'year').endOf('year').toDate(),
    };
};

export const languageValidators: FieldValidator[] = [
    {
        fieldName: 'appointmentType.value',
        fieldLabel: 'Type of appointment',
        validateFunctions: [validateRequiredField],
    },
    {
        fieldName: 'languageCertification.value',
        fieldLabel: 'Language certification',
        validateFunctions: [validateRequiredField],
    },
    {
        fieldName: 'language.value',
        fieldLabel: 'Language',
        validateFunctions: [validateRequiredField],
    },
    {
        fieldName: 'appointmentDate',
        fieldLabel: 'Appointment Date',
        validateFunctions: [
            validateDateField(
                () => referralModel.languageModel.state.get().appointmentDateString,
                appointmentDateRange,
            ),
            validateRequiredField,
        ],
    },
    {
        fieldName: 'appointmentTimeString',
        fieldLabel: 'Appointment Time',
        validateFunctions: [validateTimeFieldFromString],
    },
    {
        fieldName: 'vendorNotes',
        fieldLabel: 'Vendor Notes',
        validateFunctions: [
            requiredIfCondition(
                ({ referralModel }) => referralModel.languageModel.state.get().appointmentType?.value === 'IME',
            ),
        ]
    },
    { fieldName: 'phone', fieldLabel: 'Facility Phone Number', validateFunctions: [validatePhoneNumber] },

    { fieldName: 'address.address1', fieldLabel: 'Address 1', validateFunctions: [validateRequiredField] },
    { fieldName: 'address.city', fieldLabel: 'City', validateFunctions: [validateRequiredField] },
    { fieldName: 'address.state.value', fieldLabel: 'State', validateFunctions: [validateRequiredField] },
    { fieldName: 'address.zip', fieldLabel: 'Zip code', validateFunctions: [validateZipCode, validateRequiredField] },

    {
        fieldName: orderItemValidationErrorFieldName,
        fieldLabel: orderItemValidationErrorFieldLabel,
        validateFunctions: [(value?: string) => value || undefined],
    },
];

export class LanguageModel extends ChangeableModel<ILanguageState> {
    handleAppointmentTypeChange = async (value: TResourceValue | undefined) => {
        this.state.update((state) => {
            state.appointmentType = value;

            const errors = state.errors.find((err) => err.fieldName === 'appointmentType.value');
            if (errors) {
                errors.errors = [];
            }
        });
    };

    handleLanguageCertificationChange = async (value: TResourceValue | undefined) => {
        this.state.update((state) => {
            state.languageCertification = value;

            const errors = state.errors.find((err) => err.fieldName === 'languageCertification.value');
            if (errors) {
                errors.errors = [];
            }
        });
    };

    handleLanguageChange = async (value: TResourceValue | undefined) => {
        this.state.update((state) => {
            state.language = value;

            const errors = state.errors.find((err) => err.fieldName === 'language.value');
            if (errors) {
                errors.errors = [];
            }
        });
    };

    setOrderItemValidationError = (message: string) => {
        this.state.update((state) => {
            state.orderItemValidationError = message;

            // upsert in place - preserves array order for validate()'s _.isEqual check
            const entry = state.errors.find((err) => err.fieldName === orderItemValidationErrorFieldName);
            if (entry) {
                entry.errors = [message];
            } else {
                state.errors.push({
                    fieldName: orderItemValidationErrorFieldName,
                    fieldLabel: orderItemValidationErrorFieldLabel,
                    errors: [message],
                });
            }
        });
    };

    clearOrderItemValidationError = () => {
        if (!this.state.get().orderItemValidationError) {
            return;
        }

        this.state.update((state) => {
            state.orderItemValidationError = undefined;

            const entry = state.errors.find((err) => err.fieldName === orderItemValidationErrorFieldName);
            if (entry) {
                entry.errors = [];
            }
        });
    };

    getSummaryFields = () => {
        const state = this.state.get();
        return new Map<string, string | undefined>([
            ['Language', state.language?.label],
            ['Type of Appoinment', state.appointmentType?.label],
            ['Language Certification', state.languageCertification?.label],
        ]);
    };

    getSuggestionText(): SuggestedText | undefined {
        const state = this.state.get();
        if (state.appointmentType?.value === 'IME') {
            return {
                label: 'add default instruction',
                text: IME_DEFAULT_VENDOR_NOTES,
            };
        }
    }

    override getAdditionalNotes(withSpecialInstructions: boolean = true) {
        const state = this.state.get();

        let notes: [string, string][] = [];

        const note = [
            referralModel.getGeneralInstructions(),
            this.formatAdditionalNotes(
                [{ fieldName: 'additionalInstructions', label: 'Additional instructions' }],
                sectionSystemCode,
                withSpecialInstructions,
            ),
        ];

        notes.push([state.languageCertification?.value ?? '', note.filter(n => n).join(';')]);

        return new Map<string, string>(notes);
    }
}
